import { type FrameBuilderConfigurationWithLimits } from '../configuration'
import {
  type FrameSize,
  arrayToSize,
  getFrameSize,
  isSizeWithinBounds,
} from '../size'
import {
  type FrameBuilderArtworkImage,
  type FrameBuilderGlass,
  type FrameBuilderPassepartoutColor,
  type FrameBuilderPassepartoutSize,
  type FrameBuilderProfile,
  type FrameBuilderTexture,
} from '../state'

/**
 * Determines if artwork size is valid.
 */
export const getIsArtworkSizeValid = (
  configuration: FrameBuilderConfigurationWithLimits,
  artworkWidth?: number,
  artworkHeight?: number
) => {
  if (!artworkWidth || !artworkHeight) {
    return false
  }

  // Check if width and height are within bounds (global profile minimum or maximum)
  return isSizeWithinBounds(
    [artworkWidth, artworkHeight],
    [configuration.limits.minimumSize, configuration.limits.minimumSize],
    [configuration.limits.maximumWidth, configuration.limits.maximumHeight]
  )
}

/**
 * Determines if artwork image is valid.
 */
export const getIsArtworkImageValid = (image?: FrameBuilderArtworkImage) => {
  return !!image
}

/**
 * Determines if profile is available for given sizes.
 */
export const getIsProfileAvailableForArtSize = (
  size: FrameSize,
  profile?: FrameBuilderProfile
) => {
  if (!profile) {
    return false
  }

  // Validate profile size
  return isSizeWithinBounds(
    size,
    arrayToSize([profile.minimumSize, profile.minimumSize]),
    arrayToSize([profile.maximumWidth, profile.maximumHeight])
  )
}

/**
 * Determines if texture is valid.
 */
export const getIsTextureValid = (
  profile?: FrameBuilderProfile,
  texture?: FrameBuilderTexture
) => {
  if (!texture) {
    return false
  }

  // Validate texture to profile availability
  if (!profile) {
    return true
  }

  return !!texture.availableFor?.some(
    (linkedProfile) => linkedProfile.profile._ref === profile._id
  )
}

/**
 * Determines if profile is valid.
 */
export const getIsProfileValid = (
  artworkWidth?: number,
  artworkHeight?: number,
  profile?: FrameBuilderProfile
) => {
  if (!profile) {
    return false
  }

  // Validate profile size
  const frameSize = getFrameSize(artworkWidth, artworkHeight)
  return getIsProfileAvailableForArtSize(frameSize, profile)
}

/**
 * Determines if frame size fits within passepartout color's and profile's maximum dimensions.
 */
export const getIsPassepartoutSizeValid = (
  artworkWidth?: number,
  artworkHeight?: number,
  passepartoutSize?: FrameBuilderPassepartoutSize,
  passepartoutColor?: FrameBuilderPassepartoutColor,
  profile?: FrameBuilderProfile
) => {
  if (!passepartoutSize) {
    return false
  }

  // Validate profile minimum and maximum size
  const frameSize = getFrameSize(
    artworkWidth,
    artworkHeight,
    passepartoutSize?.size
  )

  if (profile && !getIsProfileAvailableForArtSize(frameSize, profile)) {
    return false
  }

  // Validate passepartout maximum size
  if (passepartoutSize.size === 0) {
    return true
  }

  if (!passepartoutColor?.maximumWidth || !passepartoutColor?.maximumHeight) {
    return true
  }

  if (!frameSize[0] || !frameSize[1]) {
    return true
  }

  return (
    (frameSize[0] <= passepartoutColor.maximumWidth &&
      frameSize[1] <= passepartoutColor.maximumHeight) ||
    (frameSize[0] <= passepartoutColor.maximumHeight &&
      frameSize[1] <= passepartoutColor.maximumWidth)
  )
}

/**
 * Determines if glass selection is valid.
 */
export const getIsGlassValid = (glass?: FrameBuilderGlass) => {
  return !!glass
}

/**
 * Determines if passepartout color is required and is available for profile.
 */
export const getIsPassepartoutColorValid = (
  passepartoutSize?: FrameBuilderPassepartoutSize,
  passepartoutColor?: FrameBuilderPassepartoutColor,
  profile?: FrameBuilderProfile
) => {
  if (passepartoutSize?.size === 0) {
    return true
  }

  // If passepartout has a selected size, validate that a color is selected as well
  if (!passepartoutColor) {
    return false
  }

  // Validate passepartout color to profile availability
  if (!profile) {
    return true
  }

  return !!passepartoutColor.availableFor?.some(
    (linkedProfile) => profile._id === linkedProfile?._id
  )
}

/**
 * Determines if passepartout color can be selected.
 */
export const getIsPassepartoutColorEnabled = (
  passepartoutSize?: FrameBuilderPassepartoutSize,
  passepartoutColor?: FrameBuilderPassepartoutColor,
  profile?: FrameBuilderProfile
) => {
  if (
    typeof passepartoutSize?.size === 'undefined' ||
    passepartoutSize.size === 0
  ) {
    return false
  }

  if (!profile) {
    return true
  }

  return !!passepartoutColor?.availableFor?.some(
    (linkedProfile) => profile._id === linkedProfile?._id
  )
}
